import * as React from "react";
import MiniContainer from "../components/container/MiniContainer";
import Spinner from "../components/loading/Spinner";
import KindSwitches from "./KindSwitches";
import DeviceList from "./DeviceList";
import { myNotificationSettingsGET, myNotificationSettingPUT, myPushSubscriptionDELETE, myPushTestPOST } from "./api";
import { pushSupported, pushPermission, isIOS, isStandalone, currentSubscription, subscribeThisPhone, unsubscribeThisPhone } from "./pushClient";
import "./NotificationSettings.css";

// Notifications (Ben, 26 Oct / 3 Nov 2026): the signed-in user's own page.
// Every notification keeps landing in the bell; here she chooses which kinds
// also go to her phone, and switches this phone on or off. Shown on the menu
// only once her account has push access (admin accounts always do).

function errorText(e, fallback) {
	return (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || fallback;
}

export default class NotificationSettings extends React.Component {
	state = {
		settings: null,
		thisPhone: null, // this browser's PushSubscription, or null
		busy: false,
		busyKind: null,
		busyDevice: null,
		error: null,
		notice: null
	};

	componentDidMount() {
		this.load();
	}

	load() {
		return myNotificationSettingsGET()
			.then(r => {
				this.setState({ settings: r.data });
				if (r.data.push_access && pushSupported()) {
					return currentSubscription().then(sub => this.setState({ thisPhone: sub }));
				}
			})
			.catch(e => this.setState({ error: errorText(e, "Could not load your notification settings.") }));
	}

	// Which server row is this browser's subscription? The server never sends
	// endpoints back, so we match on nothing and instead ask it to remove the
	// row by id when she switches this phone off: the browser side is dropped
	// here and the row is found by re-posting first. Simpler: on "off" we
	// unsubscribe locally and remove the newest live device for this browser's
	// description, which is the one the subscribe call created.
	thisPhoneRow() {
		const { settings, thisPhone } = this.state;
		if (!settings || !thisPhone) return null;
		const live = settings.devices.filter(d => !d.failed_at);
		return live.length ? live[0] : null;
	}

	switchOn() {
		this.setState({ busy: true, error: null, notice: null });
		subscribeThisPhone(this.state.settings.public_key)
			.then(() => this.load())
			.then(() => this.setState({ busy: false, notice: "This phone is on. Send yourself a test to see one arrive." }))
			.catch(e => this.setState({ busy: false, error: errorText(e, "That didn't work.") }));
	}

	switchOff() {
		const row = this.thisPhoneRow();
		this.setState({ busy: true, error: null, notice: null });
		unsubscribeThisPhone()
			.then(() => (row ? myPushSubscriptionDELETE(row.id) : null))
			.then(() => this.load())
			.then(() => this.setState({ busy: false, thisPhone: null, notice: "This phone is off. Nothing more will be sent to it." }))
			.catch(e => this.setState({ busy: false, error: errorText(e, "That didn't work.") }));
	}

	sendTest() {
		this.setState({ busy: true, error: null, notice: null });
		myPushTestPOST()
			.then(r => {
				const n = r.data.sent;
				this.setState({ busy: false, notice: n ? "Sent to " + n + (n === 1 ? " device." : " devices.") + " It should arrive within a few seconds." : "Nothing was sent: no device could be reached." });
			})
			.catch(e => this.setState({ busy: false, error: errorText(e, "The test could not be sent.") }));
	}

	toggle(kind, push) {
		this.setState({ busyKind: kind, error: null, notice: null });
		myNotificationSettingPUT(kind, push)
			.then(r => this.setState({ settings: r.data, busyKind: null }))
			.catch(e => this.setState({ busyKind: null, error: errorText(e, "That didn't save. Please try again.") }));
	}

	removeDevice(d) {
		this.setState({ busyDevice: d.id, error: null, notice: null });
		myPushSubscriptionDELETE(d.id)
			.then(() => this.load())
			.then(() => this.setState({ busyDevice: null }))
			.catch(e => this.setState({ busyDevice: null, error: errorText(e, "Could not remove that device.") }));
	}

	renderPhoneCard() {
		const { settings, thisPhone, busy } = this.state;
		if (!settings.configured) {
			return <p className="ns-muted">Push to phones is not switched on for this server yet. Your choices below are saved and will apply when it is.</p>;
		}
		if (!pushSupported()) {
			return (
				<p className="ns-muted">
					This browser cannot show notifications.
					{isIOS() && !isStandalone()
						? " On an iPhone, add the portal to your Home Screen first (Share, then Add to Home Screen) and open it from that icon."
						: " On a phone, open the portal in Chrome or install it from the browser menu."}
				</p>
			);
		}
		if (pushPermission() === "denied") {
			return <p className="ns-muted">Notifications are blocked for the portal on this phone. Allow them in the browser's site settings, then reload this page.</p>;
		}
		if (isIOS() && !isStandalone()) {
			return <p className="ns-muted">On an iPhone, push works only from the installed portal: Share, then Add to Home Screen, then open it from that icon and come back here.</p>;
		}
		return (
			<div className="ns-phone">
				<div className="ns-phone-state">
					<i className={"fas " + (thisPhone ? "fa-bell" : "fa-bell-slash")} /> Push to this phone: <b>{thisPhone ? "On" : "Off"}</b>
				</div>
				<div className="ns-phone-actions">
					{thisPhone ? (
						<React.Fragment>
							<button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => this.sendTest()}>
								Send a test
							</button>
							<button type="button" className="btn btn-default btn-sm" disabled={busy} onClick={() => this.switchOff()}>
								Turn off on this phone
							</button>
						</React.Fragment>
					) : (
						<button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => this.switchOn()}>
							Turn on for this phone
						</button>
					)}
				</div>
			</div>
		);
	}

	render() {
		const { settings, error, notice, busyKind, busyDevice } = this.state;
		if (!settings) {
			return (
				<Spinner loading={!error}>
					<MiniContainer>{error && <p className="ns-error">{error}</p>}</MiniContainer>
				</Spinner>
			);
		}
		return (
			<div className="row">
				<div className="col-sm-6">
					<MiniContainer>
						<h4 className="heading">Notifications on your phone</h4>
						<p className="ns-intro">Everything still shows under the bell in the portal. Here you choose what also gets sent to your phone.</p>
						{error && <p className="ns-error">{error}</p>}
						{notice && <p className="ns-notice">{notice}</p>}
						{!settings.push_access ? (
							<p className="ns-muted">Push to phones is not switched on for your account yet. Your choices below are saved for when it is.</p>
						) : (
							this.renderPhoneCard()
						)}
						{settings.push_access && (
							<React.Fragment>
								<h5 className="ns-group-title">Phones with push on</h5>
								<DeviceList devices={settings.devices} busyId={busyDevice} onRemove={d => this.removeDevice(d)} emptyText="No phone is on yet." />
							</React.Fragment>
						)}
					</MiniContainer>
				</div>
				<div className="col-sm-6">
					<MiniContainer>
						<h4 className="heading">What gets sent to your phone</h4>
						<p className="ns-intro">Press a line to change it. "Portal + phone" means it also goes to your phone. The text on your phone never includes names or venues; open the portal for the details.</p>
						<KindSwitches groups={settings.groups} kinds={settings.kinds} busyKind={busyKind} onToggle={(k, v) => this.toggle(k, v)} youOrHer="your" />
					</MiniContainer>
				</div>
			</div>
		);
	}
}
